<script setup lang="ts">
import { computed, onMounted, ref, Component } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import type {
  EvaluationType,
  EvaluationListType,
} from "@/common/types/evaluation";

import { MobileMenuTheme } from "../";

import { baseAvatar, svgIcon } from "@/common/components";

interface TabListType {
  [key: string]: {
    key: string;
    icon: string;
    label: string;
    component: Component;
  };
}

const { t } = useI18n();
const router = useRouter();

const emit = defineEmits([
  "update-data",
  "add",
  "input-search",
  "search",
  "select-tab",
]);

const props = defineProps<{
  accountId: string;
  evaluationId: string | null;
  evaluation: EvaluationType | null;
  evaluations: EvaluationListType[];
  tabList: TabListType;
  menuList: any;
  isEdit: boolean;
}>();

const selectedTab = ref<string | null>(null);
const selectedCode = ref<string | null>(null);

const tab = computed(() => {
  if (!selectedTab.value) return null;

  return props.tabList[selectedTab.value as string] || null;
});

function selectTab(tab: string) {
  selectedTab.value = tab;
}

function closeSelected() {
  const tab = selectedTab.value as string;
  selectedCode.value = null;
  selectTab("");
  setTimeout(() => selectTab(tab), 25);
}

function limitText(text: string) {
  if (text.length > 25) {
    return text.substring(0, 25) + "...";
  }
  return text;
}

onMounted(() => {
  selectTab("info");
});
</script>

<template>
  <mobile-menu-theme
    :menu-list="props.menuList"
    :tab-active="selectedTab"
    :account-id="props.accountId"
    :show-home="false"
    :show-back="true"
    :back-url="`/${props.accountId}/evaluate`"
    content-padding="0"
    @open-menu="selectTab"
  >
    <template #content>
      <div class="evaluation">
        <template v-if="evaluation">
          <div class="evaluation-title">
            <base-avatar
              :size="'2rem'"
              :url="evaluation.company?.avatar?.small"
            />
            <div class="infos">
              <div class="code">
                {{ evaluation.code }}
              </div>
              <template v-if="evaluation.company">
                <div class="company">
                  {{ limitText(evaluation.company?.name) }}
                </div>
              </template>
            </div>
          </div>
        </template>

        <template v-if="tab">
          <div class="evaluation-tab">
            <div class="title">
              <svg-icon :icon="tab.icon" size="2rem" />
              {{ tab.label }}
              <template v-if="selectedCode"> - {{ selectedCode }} </template>
            </div>
            <template v-if="selectedCode">
              <div class="actions">
                <button class="btn btn-back btn-sm" @click="closeSelected()">
                  <svg-icon icon="close" size="1.25rem" />
                </button>
              </div>
            </template>
          </div>
        </template>

        <div class="evaluation-content">
          <template v-if="selectedTab">
            <component
              :is="tabList[selectedTab]?.component"
              :is-edit="isEdit"
              :account-id="accountId"
              :evaluation-id="evaluation?.evaluation_id"
              :data="evaluation"
              :show-title="false"
              @update-data="$emit('update-data')"
              @selected-code="selectedCode = $event"
            />
          </template>
        </div>
      </div>
    </template>
  </mobile-menu-theme>
</template>

<style lang="scss" scoped>
.evaluation {
  display: flex;
  flex-direction: column;

  &-title {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
    height: 5rem;
    min-height: 5rem;
    background: var(--white-off);
    padding-inline: 1rem;

    .avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 3rem;
      height: 3rem;
      background: var(--gray);
      border-radius: 50%;
      overflow: hidden;
      color: var(--white);
    }

    .infos {
      display: flex;
      flex-direction: column;

      .code {
        color: var(--text);
        font-size: 1.1rem;
        font-weight: bold;
        text-transform: uppercase;
      }

      .company {
        color: var(--text);
        font-size: 1rem;
        text-transform: uppercase;
      }
    }
  }

  &-tab {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    height: 4rem;
    min-height: 4rem;
    background: var(--primary);
    padding-inline: 1.5rem;

    .title {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.75rem;
      text-transform: uppercase;
      color: var(--white);
      font-size: 1.25rem;
    }
  }

  &-content {
    padding-inline: 1rem;
    padding-block: 1.5rem;
  }
}
</style>
